import { useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { CurrencyCode } from "../API";
import { BookkeepingFull, TaxRate } from "../models";
import { formatDate, getBookkeepingYear } from "../utils";
import { deriveTaxAmount, getTaxRatesFor } from "../utils/receipt-upload";
import DateSelector from "./date-selector";
import { Select, Option as SelectOption } from "./select";

export type VerifyReceiptTax = {
  taxRateId: string | undefined;
  totalAmount: number | null;
  // The VAT the receipt itself states, as extracted by OCR. It wins over our
  // own calculation — Vorsteuer is deducted per the supplier's invoice, and
  // Swiss receipts routinely disagree with rausrechnen because of per-line
  // rounding (FAIRTIQ: 35.80 @ 8.1% prints 2.70, computed gives 2.68).
  // Nulled as soon as the user changes anything it depends on, from which point
  // it derives (same convention as utils.ts getTaxAmount: stored ?? computed).
  amount: number | null;
};

export type VerifyReceiptValues = {
  paidAt: Date | undefined;
  title: string;
  amount: number | null;
  currencyCode: CurrencyCode;
  bookingAccountId: string | undefined;
  transactionReferenceId: string | undefined;
  taxes: VerifyReceiptTax[];
};

export type VerifyReceiptResult = {
  paidAt: Date;
  title: string;
  amount: number;
  currencyCode: CurrencyCode;
  bookingAccountId: string;
  transactionReferenceId: string | null;
  taxes: { taxRateId: string; amount: number | null; totalAmount: number | null }[];
};

// Drives copy only; readOnly is orthogonal, mirroring the app where the save
// label follows the status (detail_screen.dart:681) while editingEnabled
// merely disables the fields and the button.
//   create  fresh drop (default — existing call sites stay untouched)
//   verify  extracted row: confirm flow, „Bestätigen"
//   edit    any other row: „Transaktion bearbeiten" / „Speichern"
export type VerifyReceiptMode = "create" | "verify" | "edit";

const modalHeaders: Record<VerifyReceiptMode, string> = {
  create: "Beleg prüfen",
  verify: "Beleg prüfen",
  edit: "Transaktion bearbeiten",
};

const saveLabels: Record<VerifyReceiptMode, string> = {
  create: "Speichern",
  verify: "Bestätigen",
  edit: "Speichern",
};

type VerifyReceiptModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  initialValues: VerifyReceiptValues | undefined;
  previewUrl?: string;
  previewIsPdf?: boolean;
  // A preview is known to be on its way (attachment fetch running): open at
  // full size immediately instead of flickering from the compact box to 6xl
  // when the blob arrives.
  previewPending?: boolean;
  bookkeeping: BookkeepingFull | undefined;
  optionsBookingAccounts: SelectOption[] | undefined;
  optionsCurrencyCodes: SelectOption[] | undefined;
  // Per row owner, like the app's userBookkeeping references — the section
  // renders only when the owner has any (detail_screen.dart:217).
  optionsTransactionReferences?: SelectOption[];
  mode?: VerifyReceiptMode;
  // App parity: everything disabled, save disabled, NO explanatory banner —
  // close and the receipt preview stay live.
  readOnly?: boolean;
  onSave: (values: VerifyReceiptResult) => Promise<void>;
};

type VerifyReceiptFormProps = {
  initialValues: VerifyReceiptValues;
  bookkeeping: BookkeepingFull | undefined;
  optionsBookingAccounts: SelectOption[] | undefined;
  optionsCurrencyCodes: SelectOption[] | undefined;
  optionsTransactionReferences?: SelectOption[];
  mode: VerifyReceiptMode;
  readOnly: boolean;
  saving: boolean;
  onSubmit: (values: VerifyReceiptResult) => void;
  onClose: () => void;
};

const toNumberOrNull = (value: string): number | null => {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// What the two tax fields show. Single definition on purpose: the mount seed and
// the live handlers both need it, and when they were separate copies the seed
// kept deriving and threw away the receipt's stated VAT.
const taxFieldsFor = (
  tax: VerifyReceiptTax,
  amount: number | null,
  rates: TaxRate[]
): { gross: string; vat: string } => {
  const rate = rates.find((taxRate) => taxRate.id === tax.taxRateId)?.rate;
  return {
    gross: tax.totalAmount !== null ? tax.totalAmount.toFixed(2) : "",
    // The receipt's own figure wins until the user edits something it depends
    // on, at which point amount is nulled and this derives.
    vat:
      tax.amount !== null
        ? tax.amount.toFixed(2)
        : rate !== undefined && amount !== null
          ? deriveTaxAmount(amount, rate, tax.totalAmount).toFixed(2)
          : "",
  };
};

// Why "no tax section" is three states rather than a boolean: see taxSection.
type TaxSection =
  | { kind: "netTaxRate" }
  | { kind: "misconfigured" }
  | { kind: "ready" };

// Collapsing a split to a single rate: the transaction total is the gross again
// (single-tax invariant). createReceiptTransaction nulls totalAmount for
// single-tax rows either way, so a kept gross part only desynchronises the two.
// The receipt's stated VAT was stated for the OLD gross, so it goes with it:
// deriveTaxAmount re-bases on (totalAmount ?? transactionAmount), but handleSave
// does `tax.amount ?? derive(...)`, so a kept amount wins and books the part's
// VAT against the whole — 35.80 -> 100.00 keeps 2.70 where 8.1% of 100.00 is
// 7.49. Always understated, and read-only at a single rate, so uncorrectable.
// Only when that gross actually moves, though: a row that carried no part, or a
// part already equal to the total, still describes the same gross, and the
// stated figure dies only on a change to the rate, a gross part or the total.
const collapseToSingleRate = (
  tax: VerifyReceiptTax,
  transactionAmount: number | null
): VerifyReceiptTax => ({
  ...tax,
  totalAmount: null,
  amount:
    tax.totalAmount === null || tax.totalAmount === transactionAmount
      ? tax.amount
      : null,
});

// Mirrors app getTaxesBasedOnBookingAccount: re-resolve each tax by RATE, not
// id — the same rate is a different TaxRate record per taxType and per date
// window. Rates with no counterpart drop.
const remapTaxes = (
  current: VerifyReceiptTax[],
  fromRates: TaxRate[],
  toRates: TaxRate[],
  transactionAmount: number | null
): VerifyReceiptTax[] => {
  const next = current.flatMap((tax) => {
    const rate = fromRates.find((taxRate) => taxRate.id === tax.taxRateId)?.rate;
    const taxRateNew = toRates.find((taxRate) => taxRate.rate === rate);
    return taxRateNew ? [{ ...tax, taxRateId: taxRateNew.id }] : [];
  });
  return next.length === 1
    ? [collapseToSingleRate(next[0], transactionAmount)]
    : next;
};

// Mounted only once initialValues exist, and the modal unmounts its children
// on close — every open cycle gets a fresh mount, so useState can seed
// directly from the prop (no prop-mirroring effect).
const VerifyReceiptForm = ({
  initialValues,
  bookkeeping,
  optionsBookingAccounts,
  optionsCurrencyCodes,
  optionsTransactionReferences,
  mode,
  readOnly,
  saving,
  onSubmit,
  onClose,
}: VerifyReceiptFormProps) => {
  const [paidAt, setPaidAt] = useState<Date | undefined>(initialValues.paidAt);
  const [title, setTitle] = useState(initialValues.title);
  const [amount, setAmount] = useState(
    initialValues.amount !== null ? initialValues.amount.toFixed(2) : ""
  );
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>(
    initialValues.currencyCode
  );
  const [bookingAccountId, setBookingAccountId] = useState<string | undefined>(
    initialValues.bookingAccountId
  );
  const [transactionReferenceId, setTransactionReferenceId] = useState<
    string | undefined
  >(initialValues.transactionReferenceId);
  // Rates active on the date AND matching the booking account's taxType —
  // mirrors app getTaxRates. Without the taxType filter the same rate appears
  // once per type (2.6% Material / 2.6% Investition) and a match is arbitrary.
  // Declared above the tax state because the mount seed resolves against it.
  const taxRatesActive = useMemo(
    () =>
      bookkeeping && paidAt
        ? getTaxRatesFor(bookkeeping, paidAt, bookingAccountId)
        : [],
    [bookkeeping, paidAt, bookingAccountId]
  );

  // The seed is the one path that never resolved its rates. A fresh drop is
  // already matched against the active list (matchOcrTaxesToTaxRates), but an
  // extracted row carries taxRateIds straight off stored Tax records: a rate
  // deleted, re-dated or moved to another taxType since extraction leaves an id
  // absent from taxRatesActive. Seeded raw, the Select fell back to its
  // placeholder while isValid still passed, and handleSave's `?? 0` booked the
  // row at 0% VAT — 108.10 booking 0.00, a silent Vorsteuer loss.
  // Resolved through the same funnel as every other rate change. `fromRates` is
  // the bookkeeping's full list: a stale id is by definition not in the active
  // one, and its own rate is what the remap matches on. No counterpart → dropped,
  // leaving taxes empty, which blocks the save rather than booking a guess.
  //
  // Edit mode is the opposite case: the taxes are what is actually BOOKED, not
  // an OCR guess — remapping the seed could silently swap a stored rate record
  // (and the save diff would then rewrite the row's taxes on an unrelated
  // edit). The app keeps the stored TaxRate (fetchExistingTaxes' orElse), so
  // edit seeds verbatim; taxRatesSelectable below keeps such rates resolvable.
  const [taxes, setTaxes] = useState<VerifyReceiptTax[]>(() =>
    bookkeeping && mode !== "edit"
      ? remapTaxes(
          initialValues.taxes,
          bookkeeping.taxRates ?? [],
          taxRatesActive,
          initialValues.amount
        )
      : initialValues.taxes
  );
  // Active rates plus any rate a current row still references — a stored row
  // may point outside the active window (deleted, re-dated or re-typed since
  // booking). Everything that RESOLVES a rate (display, options, VAT derive,
  // remap source) works against this list; the misconfigured check below stays
  // on the active list, because "no rate configured for this date + taxType"
  // is about the window, not about what an old row still carries.
  const taxRatesSelectable = useMemo(() => {
    const referenced = (bookkeeping?.taxRates ?? []).filter(
      (taxRate) =>
        taxes.some((tax) => tax.taxRateId === taxRate.id) &&
        !taxRatesActive.some((activeRate) => activeRate.id === taxRate.id)
    );
    return referenced.length > 0
      ? [...taxRatesActive, ...referenced]
      : taxRatesActive;
  }, [bookkeeping, taxes, taxRatesActive]);
  // Raw field strings, mirroring the app's TextEditingControllers: the value
  // shown must survive mid-keystroke states that the derived value would clobber.
  // Seeded from the resolved taxes, not the raw prop — a dropped row would
  // otherwise leave its inputs behind and drift the two apart.
  const [taxInputs, setTaxInputs] = useState<{ gross: string; vat: string }[]>(
    () =>
      taxes.map((tax) =>
        taxFieldsFor(tax, initialValues.amount, taxRatesSelectable)
      )
  );

  // "No tax section" has two causes that want opposite handling, and a boolean
  // held both — which is why this keeps reopening. 0f5310ba added the
  // taxes.length > 0 rule; 9606a05e escaped the resulting deadlock (empty
  // picker, Speichern disabled, no way out) by returning false for empty rates,
  // reinstating the silent empty save through a different door. Both were
  // locally right. The states are:
  //
  //   netTaxRate     Saldosteuersatz for the selected date — no per-transaction
  //                  taxes exist. Hide, save []. Mirrors the app's rule.
  //   misconfigured  A VAT year, but no rate covers this date + taxType. Not
  //                  tax-free — nobody configured it. Block and say so.
  //   ready          Rates exist; the section renders and a rate is required.
  //
  // Order matters: Saldosteuersatz is checked first, because such a year
  // legitimately has no rates and must not be reported as misconfigured.
  //
  // Saving [] on a VAT year is not a cosmetic slip: the Bexio sync throws "No
  // taxes found" into a mapping with MaximumRetryAttempts 0 and no DLQ
  // (86carc3hu), so the record is dropped and the transaction sits at draft
  // forever — never synced, no signal, nothing to recover from.
  const taxSection = useMemo((): TaxSection => {
    if (!bookkeeping) return { kind: "ready" };
    const bookkeepingYear = paidAt
      ? getBookkeepingYear(bookkeeping, paidAt)
      : undefined;
    if (bookkeepingYear?.hasNetTaxRate) return { kind: "netTaxRate" };
    if (taxRatesActive.length === 0) return { kind: "misconfigured" };
    return { kind: "ready" };
  }, [bookkeeping, paidAt, taxRatesActive]);

  const showTaxes = taxSection.kind === "ready";

  const optionsTaxRates: SelectOption[] = useMemo(
    () =>
      [...taxRatesSelectable]
        .sort((a, b) => b.rate - a.rate)
        .map((taxRate) => ({ value: taxRate.id, label: `${taxRate.rate}%` })),
    [taxRatesSelectable]
  );

  const amountNumber = toNumberOrNull(amount);
  const isMultiTax = taxes.length > 1;
  // App parity: the rate select is `required: true` in a VAT year
  // (detail_screen.dart:396) — a receipt with no VAT picks the 0% rate, it does
  // not go untaxed. `every` on an empty array is true, so this needs its own
  // check or a booking-account/date remap that drops every rate saves silently.
  const taxesValid =
    taxes.length > 0 &&
    taxes.every(
      (tax) =>
        tax.taxRateId !== undefined && (!isMultiTax || tax.totalAmount !== null)
    );
  // App parity (taxZeroAmountFirst/SecondError) plus stricter: a zero gross
  // part is never a real split — and neither is a negative one. The app only
  // checks == 0, but typing a second-row gross larger than the total drives
  // the auto-adjusted first row negative while the parts still sum to the
  // total, so the sum check alone would let a negative VAT through to Bexio.
  const taxGrossPartsPositive =
    !isMultiTax || taxes.every((tax) => (tax.totalAmount ?? 0) > 0);
  // App parity (taxRatesAddUpToAmountError): the gross parts are the posting
  // split. Auto-adjust keeps them in sync while typing, but changing the
  // transaction amount afterwards can still break it.
  const taxTotalsMatchAmount =
    !isMultiTax ||
    amountNumber === null ||
    Math.abs(
      taxes.reduce((sum, tax) => sum + (tax.totalAmount ?? 0), 0) - amountNumber
    ) < 0.011;
  // Last-gate invariants, independent of how the rows came to be: the app
  // allows at most two rates (tooManyTaxesError) and a rate can only appear
  // once — two rows on one rate double the Vorsteuer for it. No UI path
  // creates either state, but state can outlive the UI that made it (seeded
  // data, hot reload in dev): a save observed live carried three rows, two of
  // them the same 8.1%.
  const taxCountValid = taxes.length <= 2;
  const seenRateIds = taxes
    .map((tax) => tax.taxRateId)
    .filter((id): id is string => id !== undefined);
  const taxRatesUnique = new Set(seenRateIds).size === seenRateIds.length;
  const isValid =
    paidAt !== undefined &&
    title.trim() !== "" &&
    amountNumber !== null &&
    amountNumber > 0 &&
    bookingAccountId !== undefined &&
    // misconfigured blocks rather than skipping the tax rules — that is the
    // whole difference between it and netTaxRate, which legitimately has none.
    taxSection.kind !== "misconfigured" &&
    (taxSection.kind === "netTaxRate" ||
      (taxesValid &&
        taxGrossPartsPositive &&
        taxTotalsMatchAmount &&
        taxCountValid &&
        taxRatesUnique));

  const handleSave = () => {
    if (
      readOnly ||
      !isValid ||
      !paidAt ||
      !bookingAccountId ||
      amountNumber === null
    ) {
      return;
    }
    onSubmit({
      paidAt,
      title: title.trim(),
      amount: amountNumber,
      currencyCode,
      bookingAccountId,
      transactionReferenceId: transactionReferenceId ?? null,
      // Hidden tax section (net-tax-rate year for the selected date) —
      // stale rows from a previous date must not be persisted.
      taxes: showTaxes
        ? taxes.map((tax) => {
            const rate =
              taxRatesSelectable.find((taxRate) => taxRate.id === tax.taxRateId)
                ?.rate ?? 0;
            return {
              taxRateId: tax.taxRateId as string,
              // The receipt's own figure if OCR read one and nothing it depends
              // on has been touched; otherwise derived.
              amount:
                tax.amount ??
                deriveTaxAmount(amountNumber, rate, tax.totalAmount),
              totalAmount: tax.totalAmount,
            };
          })
        : [],
    });
  };

  // --- Tax state -------------------------------------------------------
  // `taxes` is the model; `taxInputs` is what the fields show. They must never
  // be set apart — the display derives from the model, except for the field
  // being typed into, whose raw string has to survive (mirrors the app's
  // TextEditingControllers: a derived value rewrites "2.7" to "2.00"
  // mid-keystroke). commitTaxes is the ONLY place that writes either, so the
  // two cannot drift.
  const commitTaxes = (
    next: VerifyReceiptTax[],
    options?: {
      amount?: number | null;
      rates?: TaxRate[];
      raw?: { index: number; gross?: string; vat?: string };
    }
  ) => {
    const amountFor = options?.amount !== undefined ? options.amount : amountNumber;
    const ratesFor = options?.rates ?? taxRatesSelectable;
    setTaxes(next);
    setTaxInputs(
      next.map((tax, i) => {
        const fields = taxFieldsFor(tax, amountFor, ratesFor);
        return options?.raw?.index === i
          ? {
              ...fields,
              ...(options.raw.gross !== undefined && { gross: options.raw.gross }),
              ...(options.raw.vat !== undefined && { vat: options.raw.vat }),
            }
          : fields;
      })
    );
  };

  const rateOf = (taxRateId: string | undefined) =>
    taxRatesSelectable.find((taxRate) => taxRate.id === taxRateId)?.rate;

  // A split is one constraint system: g1 + g2 = total, and each rate fixes
  // gross <-> VAT. Given the total and the rates there is exactly one free
  // value, so writing any field recomputes the rest. Receipts print VAT per rate
  // rather than gross, so both ends are enterable.
  const applySplit = (
    index: number,
    gross: number | null,
    raw: { gross?: string; vat?: string }
  ) =>
    commitTaxes(
      taxes.map((tax, i) => {
        // Any edit invalidates the receipt's stated VAT for both rows — the
        // gross each rate covers has changed.
        if (i === index) return { ...tax, totalAmount: gross, amount: null };
        if (taxes.length !== 2) return tax;
        // Sibling takes the remainder — app onChanged, detail_screen.dart:520 —
        // clamped at 0: typing a gross larger than the total (routinely a
        // transient while keying digits) would otherwise flash a negative
        // remainder into the sibling row. Zero fails the positive-parts rule,
        // so the save stays blocked either way.
        return {
          ...tax,
          amount: null,
          totalAmount:
            amountNumber !== null
              ? Math.max(0, Number((amountNumber - (gross ?? 0)).toFixed(2)))
              : null,
        };
      }),
      { raw: { index, ...raw } }
    );

  const setTaxGross = (index: number, raw: string, gross: number | null) =>
    applySplit(index, gross, { gross: raw });

  // Inverse of deriveTaxAmount: gross = VAT * (100 + r) / r. A 0% rate carries
  // no VAT, so there is nothing to invert.
  const setTaxVat = (index: number, raw: string, vat: number | null) => {
    const rate = rateOf(taxes[index]?.taxRateId);
    if (rate === undefined || rate === 0) return;
    applySplit(
      index,
      vat === null ? null : Number(((vat * (100 + rate)) / rate).toFixed(2)),
      { vat: raw }
    );
  };

  // Changing the rate invalidates the receipt's stated VAT for that row.
  const setTaxRate = (index: number, taxRateId: string | undefined) =>
    commitTaxes(
      taxes.map((tax, i) =>
        i === index ? { ...tax, taxRateId, amount: null } : tax
      )
    );

  // App: adding a second rate splits the total — the existing part takes the
  // whole amount, the new one the remainder.
  const addTax = () =>
    commitTaxes(
      taxes.length === 1
        ? [
            { ...taxes[0], totalAmount: amountNumber ?? 0, amount: null },
            { taxRateId: undefined, totalAmount: 0, amount: null },
          ]
        : [...taxes, { taxRateId: undefined, totalAmount: null, amount: null }]
    );

  // Back to a single rate: the transaction total is the gross again. Mirrors the
  // app's onDelete (detail_screen.dart), which rebuilds the survivor as a bare
  // TaxTemp(taxRate) — "Replace remaining tax with empty amounts", both null.
  const removeTax = (index: number) => {
    const remaining = taxes.filter((_, i) => i !== index);
    commitTaxes(
      remaining.length === 1
        ? [collapseToSingleRate(remaining[0], amountNumber)]
        : remaining
    );
  };

  // The receipt's VAT belonged to the old total — same rule as clearing FX when
  // the amount changes. Gross parts stay put (app parity: the sum validator
  // fires instead).
  const setAmountAndTaxes = (valueString: string) => {
    setAmount(valueString);
    commitTaxes(
      taxes.map((tax) => ({ ...tax, amount: null })),
      { amount: toNumberOrNull(valueString) }
    );
  };

  // Rate windows move with the date (7.7% -> 8.1% in 2024): a tax pointing at a
  // rate that is not active on the new date would save its stale id and derive
  // VAT at rate 0.
  // Remap sources are the SELECTABLE rates: a stored-but-inactive rate still
  // knows its percentage, so a date/account change can carry it over to the
  // new window's record of the same rate (app getTaxesBasedOnBookingAccount
  // matches by rate value on the TaxTemp's resolved record).
  const setPaidAtAndTaxes = (date?: Date) => {
    setPaidAt(date);
    if (!bookkeeping || !date) return;
    const ratesNew = getTaxRatesFor(bookkeeping, date, bookingAccountId);
    commitTaxes(remapTaxes(taxes, taxRatesSelectable, ratesNew, amountNumber), {
      rates: ratesNew,
    });
  };

  const handleBookingAccountChange = (newBookingAccountId?: string) => {
    setBookingAccountId(newBookingAccountId);
    if (!bookkeeping || !paidAt) return;
    const ratesNew = getTaxRatesFor(bookkeeping, paidAt, newBookingAccountId);
    // taxRatesSelectable still holds the previous account's rates on this render.
    commitTaxes(remapTaxes(taxes, taxRatesSelectable, ratesNew, amountNumber), {
      rates: ratesNew,
    });
  };

  return (
    <VStack spacing="4" alignItems="stretch" flex="1">
      <VStack spacing="1" alignItems="stretch">
        <Text fontSize="sm" fontWeight="medium" color="gray.700">
          Datum
        </Text>
        <DateSelector
          defaultValue={paidAt}
          onChange={setPaidAtAndTaxes}
          isDisabled={readOnly}
          width="full"
        />
      </VStack>
      <VStack spacing="1" alignItems="stretch">
        <Text fontSize="sm" fontWeight="medium" color="gray.700">
          Titel
        </Text>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          isInvalid={title.trim() === ""}
          isDisabled={readOnly}
        />
      </VStack>
      <HStack alignItems="end" spacing="2">
        <VStack spacing="1" alignItems="stretch" flex="1">
          <Text fontSize="sm" fontWeight="medium" color="gray.700">
            Betrag
          </Text>
          <NumberInput
            value={amount}
            onChange={setAmountAndTaxes}
            precision={2}
            isInvalid={amountNumber === null || amountNumber <= 0}
            isDisabled={readOnly}
          >
            <NumberInputField />
          </NumberInput>
        </VStack>
        <Select
          value={optionsCurrencyCodes?.find(
            (option) => option.value === currencyCode
          )}
          options={optionsCurrencyCodes}
          onChange={(option) =>
            option && setCurrencyCode(option.value as CurrencyCode)
          }
          isDisabled={readOnly}
          width="110px"
        />
      </HStack>
      <VStack spacing="1" alignItems="stretch">
        <Text fontSize="sm" fontWeight="medium" color="gray.700">
          Buchungskategorie
        </Text>
        <Select
          value={optionsBookingAccounts?.find(
            (option) => option.value === bookingAccountId
          )}
          options={optionsBookingAccounts}
          onChange={(option) =>
            handleBookingAccountChange(option?.value as string | undefined)
          }
          isValid={bookingAccountId !== undefined}
          placeholder="Auswählen..."
          isDisabled={readOnly}
          width="full"
        />
      </VStack>
      {/* Only when the owner has references — app detail_screen.dart:217. */}
      {optionsTransactionReferences &&
        optionsTransactionReferences.length > 0 && (
          <VStack spacing="1" alignItems="stretch">
            <Text fontSize="sm" fontWeight="medium" color="gray.700">
              Referenz (Kunde, Ort, etc.)
            </Text>
            <Select
              value={optionsTransactionReferences.find(
                (option) => option.value === transactionReferenceId
              )}
              options={optionsTransactionReferences}
              onChange={(option) =>
                setTransactionReferenceId(option?.value as string | undefined)
              }
              placeholder="Referenz auswählen"
              isDisabled={readOnly}
              width="full"
            />
          </VStack>
        )}
      {/* A VAT year with no rate for this date + taxType. Says which two inputs
          decide it, because both are on this form and a non-admin can act on
          them; the Einstellungen line is the admin's route. Hiding this is what
          let taxes: [] look deliberate. Suppressed when read-only: the warning
          asks for an edit the gate forbids, and the app shows nothing here. */}
      {taxSection.kind === "misconfigured" && !readOnly && (
        <Alert status="warning" borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Kein gültiger Steuersatz</AlertTitle>
            <AlertDescription fontSize="sm">
              {optionsBookingAccounts?.find(
                (option) => option.value === bookingAccountId
              )?.label ? (
                <>
                  Für «
                  {
                    optionsBookingAccounts.find(
                      (option) => option.value === bookingAccountId
                    )?.label
                  }
                  » ist {paidAt ? `am ${formatDate(paidAt)} ` : ""}kein
                  Steuersatz hinterlegt.
                </>
              ) : (
                <>
                  {paidAt ? `Am ${formatDate(paidAt)} ist ` : "Es ist "}kein
                  Steuersatz hinterlegt.
                </>
              )}{" "}
              Ändere das Datum oder die Buchungskategorie — oder ergänze den
              Steuersatz in den Einstellungen.
            </AlertDescription>
          </Box>
        </Alert>
      )}
      {showTaxes && (
        <VStack spacing="1" alignItems="stretch">
          <HStack justifyContent="space-between">
            <Text fontSize="sm" fontWeight="medium" color="gray.700">
              MwSt.
            </Text>
            {/* App supports one or two rates (multipleTaxes / taxes[1]). */}
            {taxes.length < 2 && (
              <Button
                size="xs"
                variant="ghost"
                leftIcon={<AddIcon boxSize="2.5" />}
                onClick={addTax}
                isDisabled={readOnly}
              >
                Satz hinzufügen
              </Button>
            )}
          </HStack>
          {taxes.length === 0 && (
            <Text fontSize="sm" color="gray.500">
              Keine MwSt. erfasst
            </Text>
          )}
          {taxes.length > 0 && (
            <HStack spacing="2" pb="0.5">
              <Text fontSize="xs" color="gray.500" width="110px">
                Satz
              </Text>
              {isMultiTax && (
                <Text fontSize="xs" color="gray.500" flex="1">
                  Betrag mit MwSt.
                </Text>
              )}
              <Text fontSize="xs" color="gray.500" flex="1">
                davon MwSt.
              </Text>
              <Box width="32px" />
            </HStack>
          )}
          {taxes.map((tax, index) => (
            <HStack key={index} spacing="2">
              <Select
                value={optionsTaxRates.find(
                  (option) => option.value === tax.taxRateId
                )}
                options={optionsTaxRates}
                onChange={(option) =>
                  setTaxRate(index, option?.value as string | undefined)
                }
                isValid={tax.taxRateId !== undefined}
                placeholder="Satz"
                isDisabled={readOnly}
                width="110px"
              />
              {/* Single rate: the gross IS the transaction total, so there is
                  nothing to enter — the app shows no field here either. */}
              {isMultiTax && (
                <NumberInput
                  value={taxInputs[index]?.gross ?? ""}
                  onChange={(valueString) =>
                    setTaxGross(index, valueString, toNumberOrNull(valueString))
                  }
                  precision={2}
                  isInvalid={
                    tax.totalAmount === null || tax.totalAmount === 0
                  }
                  isDisabled={readOnly}
                  flex="1"
                >
                  <NumberInputField placeholder="Betrag mit MwSt." />
                </NumberInput>
              )}
              {/* Editable only when a split exists: with one rate the gross is
                  pinned to the total, so VAT has no free variable to solve. */}
              <NumberInput
                value={taxInputs[index]?.vat ?? ""}
                onChange={(valueString) =>
                  setTaxVat(index, valueString, toNumberOrNull(valueString))
                }
                precision={2}
                isReadOnly={!isMultiTax || rateOf(tax.taxRateId) === 0}
                isDisabled={readOnly}
                flex="1"
              >
                <NumberInputField
                  placeholder="MwSt."
                  bg={
                    !isMultiTax || rateOf(tax.taxRateId) === 0
                      ? "gray.50"
                      : undefined
                  }
                />
              </NumberInput>
              <IconButton
                aria-label="MwSt. entfernen"
                icon={<DeleteIcon boxSize="3.5" />}
                size="sm"
                variant="ghost"
                onClick={() => removeTax(index)}
                isDisabled={readOnly}
              />
            </HStack>
          ))}
          {isMultiTax && !taxGrossPartsPositive && (
            <Text fontSize="sm" color="error.500">
              Die Beträge mit MwSt. müssen grösser als 0 sein
            </Text>
          )}
          {!taxTotalsMatchAmount && (
            <Text fontSize="sm" color="error.500">
              Die Beträge mit MwSt. müssen zusammen dem Totalbetrag entsprechen
            </Text>
          )}
          {!taxCountValid && (
            <Text fontSize="sm" color="error.500">
              Maximal zwei Steuersätze sind erlaubt
            </Text>
          )}
          {!taxRatesUnique && (
            <Text fontSize="sm" color="error.500">
              Jeder Steuersatz darf nur einmal vorkommen
            </Text>
          )}
        </VStack>
      )}
      <HStack justifyContent="end" spacing="3" pt="2">
        <Button variant="outline" onClick={onClose} isDisabled={saving}>
          Abbrechen
        </Button>
        <Button
          colorScheme="primary"
          onClick={handleSave}
          // Read-only shows the disabled save instead of hiding it — same as
          // the app, which nulls onPressed but keeps the button visible.
          isDisabled={!isValid || readOnly}
          isLoading={saving}
        >
          {saveLabels[mode]}
        </Button>
      </HStack>
    </VStack>
  );
};

const VerifyReceiptModal = ({
  isOpen,
  onClose,
  isLoading,
  initialValues,
  previewUrl,
  previewIsPdf,
  previewPending,
  bookkeeping,
  optionsBookingAccounts,
  optionsCurrencyCodes,
  optionsTransactionReferences,
  mode = "create",
  readOnly = false,
  onSave,
}: VerifyReceiptModalProps) => {
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (values: VerifyReceiptResult) => {
    setSaving(true);
    try {
      await onSave(values);
    } finally {
      setSaving(false);
    }
  };

  const withPreview = !!previewUrl || !!previewPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={withPreview ? "6xl" : "lg"}
      closeOnOverlayClick={false}
      // Closing mid-save would let the parent treat the drop as abandoned
      // and create a duplicate extracted transaction.
      closeOnEsc={!saving}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      {/* With a preview (present or on its way) the modal takes the viewport
          height so the whole receipt is readable — the point of verifying is
          checking the form against the document. Without one, the compact
          centered box stays. */}
      <ModalContent h={withPreview ? "92vh" : undefined} maxH="92vh">
        <ModalHeader>{modalHeaders[mode]}</ModalHeader>
        <ModalCloseButton isDisabled={saving} />
        {/* md+: body height is fixed and the form column scrolls internally.
            base: preview and form stack vertically — the body itself must
            scroll, or everything below the 300px preview (including
            Speichern) is clipped with no way to reach it. */}
        <ModalBody
          pb="4"
          display="flex"
          flexDirection="column"
          overflow={{ base: "auto", md: "hidden" }}
        >
          {isLoading || !initialValues ? (
            <Center py="16" flex="1">
              <VStack spacing="4">
                <Spinner size="lg" color="primary.500" />
                <Text color="gray.600">Beleg wird analysiert…</Text>
              </VStack>
            </Center>
          ) : (
            <Flex
              gap="6"
              direction={{ base: "column", md: "row" }}
              flex="1"
              minH="0"
            >
              {withPreview && !previewUrl && (
                <Center flex="1" minH={{ base: "300px", md: "0" }}>
                  <Spinner size="lg" color="primary.500" />
                </Center>
              )}
              {previewUrl && (
                <Flex flex="1" minH={{ base: "300px", md: "0" }}>
                  {previewIsPdf ? (
                    // iframe, not embed: Safari renders an <embed> PDF as a
                    // frozen first page (no scroll); in an iframe it uses its
                    // real PDF viewer with scrolling for multi-page Belege.
                    <iframe
                      title="Beleg"
                      src={previewUrl}
                      width="100%"
                      height="100%"
                      style={{ border: "none", borderRadius: "8px" }}
                    />
                  ) : (
                    <Image
                      src={previewUrl}
                      alt="Beleg"
                      objectFit="contain"
                      width="100%"
                      height="100%"
                      borderRadius="8px"
                      bg="gray.50"
                    />
                  )}
                </Flex>
              )}
              {/* Fixed-width form column: the receipt gets the remaining space.
                  my=auto centers the form vertically while it is shorter than
                  the receipt; once it grows past the height it scrolls. */}
              <Flex
                direction="column"
                w={{ base: "full", md: "420px" }}
                flexShrink={0}
                overflowY={{ base: "visible", md: "auto" }}
              >
                <Box my="auto">
                  <VerifyReceiptForm
                    initialValues={initialValues}
                    bookkeeping={bookkeeping}
                    optionsBookingAccounts={optionsBookingAccounts}
                    optionsCurrencyCodes={optionsCurrencyCodes}
                    optionsTransactionReferences={optionsTransactionReferences}
                    mode={mode}
                    readOnly={readOnly}
                    saving={saving}
                    onSubmit={handleSubmit}
                    onClose={onClose}
                  />
                </Box>
              </Flex>
            </Flex>
          )}
        </ModalBody>
        {(isLoading || !initialValues) && (
          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default VerifyReceiptModal;
